import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { useMonitorConfig } from '../hooks/useMonitorConfig';
import ConfigMonitorUsoModal from '../components/ConfigMonitorUsoModal';

const SISTEMAS = [
  { key: 'rh',         label: 'Portal RH' },
  { key: 'marketing',  label: 'Marketing' },
  { key: 'financeiro', label: 'Financeiro' },
  { key: 'ti',         label: 'Portal TI' },
  { key: 'logistica',  label: 'Logistica' },
];

const SISTEMA_LABEL = SISTEMAS.reduce((acc, s) => { acc[s.key] = s.label; return acc; }, {});

function corDoStatus(seg, verdeAte, amareloAte) {
  if (seg < verdeAte) return 'verde';
  if (seg < amareloAte) return 'amarelo';
  return 'cinza';
}

function tempoRelativo(seg) {
  if (seg < 60) return `há ${seg}s`;
  if (seg < 3600) return `há ${Math.floor(seg / 60)}min`;
  return `há ${Math.floor(seg / 3600)}h`;
}

function duracaoSessao(primeiroIso, ultimoIso) {
  if (!primeiroIso || !ultimoIso) return '—';
  const ms = new Date(ultimoIso).getTime() - new Date(primeiroIso).getTime();
  const min = Math.max(0, Math.floor(ms / 60000));
  if (min < 1) return '< 1min';
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  const m = min - h * 60;
  return m ? `${h}h${m}min` : `${h}h`;
}

export default function UsoSistemas() {
  const { config, atualizar, resetar, recarregar: recarregarConfig, erro: erroConfig, PRESET } = useMonitorConfig();

  const [sessoes, setSessoes]   = useState([]);
  const [filiais, setFiliais]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [erro, setErro]         = useState('');
  const [agora, setAgora]       = useState(Date.now());
  const [configAberta, setConfigAberta] = useState(false);

  const [sistemaFiltro, setSistemaFiltro] = useState('');
  const [filialFiltro, setFilialFiltro]   = useState('');
  const [usuarioFiltro, setUsuarioFiltro] = useState('');

  const carregando = useRef(false);

  const carregar = useCallback(async () => {
    if (carregando.current) return;
    carregando.current = true;
    try {
      const params = {};
      if (sistemaFiltro) params.sistema = sistemaFiltro;
      if (filialFiltro)  params.filial  = filialFiltro;
      if (usuarioFiltro.trim()) params.usuario = usuarioFiltro.trim();

      const [online, fils] = await Promise.all([
        axios.get(`${API}/uso-sistemas/online`,  { params }),
        axios.get(`${API}/uso-sistemas/filiais`),
      ]);

      setSessoes(online.data?.data || []);
      setFiliais(fils.data?.data || []);
      setAgora(Date.now());
      setErro('');
      // Refresh da config junto: se alguem mudou em outra estacao, propaga ate 15s depois.
      recarregarConfig();
    } catch (_) {
      setErro('Nao foi possivel carregar o monitor de uso.');
    } finally {
      setLoading(false);
      carregando.current = false;
    }
  }, [sistemaFiltro, filialFiltro, usuarioFiltro, recarregarConfig]);

  useEffect(() => {
    carregar();
    if (config.pollingSeg <= 0) return undefined;
    const id = setInterval(carregar, config.pollingSeg * 1000);
    return () => clearInterval(id);
  }, [carregar, config.pollingSeg]);

  // Tick a cada segundo pra atualizar tempo relativo sem refazer rede.
  useEffect(() => {
    const id = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Aplica config (janela ativa, esconde cinzas) sobre as sessoes que vieram
  // do backend (que ja serve uma janela ampla de 2h). Calcula tudo client-side
  // pra refletir mudanca de config instantaneamente, sem refetch.
  const sessoesVisiveis = useMemo(() => {
    const janelaSeg = config.janelaAtivoMin * 60;
    return sessoes
      .map((s) => {
        const segServidor = s.segundos_desde_ping || 0;
        const segLocal = Math.max(
          segServidor,
          Math.floor((agora - new Date(s.ultimo_heartbeat).getTime()) / 1000)
        );
        const cor = corDoStatus(segLocal, config.verdeAteSeg, config.amareloAteSeg);
        return { ...s, _seg: segLocal, _cor: cor };
      })
      .filter((s) => {
        if (s._seg > janelaSeg) return false;
        if (config.esconderCinzas && s._cor === 'cinza') return false;
        return true;
      });
  }, [sessoes, agora, config.janelaAtivoMin, config.verdeAteSeg, config.amareloAteSeg, config.esconderCinzas]);

  const resumoPorSistema = useMemo(() => {
    const map = new Map();
    SISTEMAS.forEach((s) => map.set(s.key, 0));
    sessoesVisiveis.forEach((s) => {
      map.set(s.sistema, (map.get(s.sistema) || 0) + 1);
    });
    return map;
  }, [sessoesVisiveis]);

  const totalOnline = sessoesVisiveis.length;
  const sistemasComGente = [...resumoPorSistema.values()].filter((n) => n > 0).length;

  return (
    <div className={`content-shell ${config.modoCompacto ? 'monitor-uso--compacto' : ''}`}>
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">Monitor</span>
          <h1>Uso dos Sistemas</h1>
          <p>
            Quem esta interagindo com cada sistema interno do Centrao agora.
            Janela ativa: <strong>{config.janelaAtivoMin}min</strong>.
            Atualizacao: <strong>{config.pollingSeg > 0 ? `${config.pollingSeg}s` : 'manual'}</strong>.
            Dados brutos ficam por 2h na tabela.
          </p>
        </div>
        <div className="page-header__actions">
          <button className="btn btn-secondary" type="button" onClick={carregar} disabled={loading}>
            <i className="bi bi-arrow-clockwise" /> Atualizar
          </button>
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={() => setConfigAberta(true)}
            title="Configuracoes do painel"
          >
            <i className="bi bi-sliders" /> Configurar
          </button>
        </div>
      </section>

      {config.mostrarResumo ? (
        <section className="monitor-uso__resumo">
          <div className="monitor-uso__card monitor-uso__card--total">
            <span className="monitor-uso__card-eyebrow">Total ativo</span>
            <strong className="monitor-uso__card-valor">{totalOnline}</strong>
            <span className="monitor-uso__card-sub">
              em {sistemasComGente} sistema{sistemasComGente === 1 ? '' : 's'}
            </span>
          </div>
          {SISTEMAS.map((s) => {
            const usuarios = resumoPorSistema.get(s.key) || 0;
            return (
              <div key={s.key} className={`monitor-uso__card ${usuarios ? '' : 'monitor-uso__card--vazio'}`}>
                <span className="monitor-uso__card-eyebrow">{s.label}</span>
                <strong className="monitor-uso__card-valor">{usuarios}</strong>
                <span className="monitor-uso__card-sub">
                  {usuarios === 0 ? 'ninguem' : usuarios === 1 ? '1 pessoa' : `${usuarios} pessoas`}
                </span>
              </div>
            );
          })}
        </section>
      ) : null}

      <div className="filter-card">
        <div className="filter-card__field">
          <select value={sistemaFiltro} onChange={(e) => setSistemaFiltro(e.target.value)}>
            <option value="">Todos os sistemas</option>
            {SISTEMAS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <div className="filter-card__field">
          <select value={filialFiltro} onChange={(e) => setFilialFiltro(e.target.value)}>
            <option value="">Todas as filiais</option>
            {filiais.map((f) => <option key={f} value={f}>Filial {f}</option>)}
          </select>
        </div>
        <div className="filter-card__field filter-card__field--grow">
          <i className="bi bi-search" />
          <input
            type="text"
            placeholder="Buscar usuario por nome ou id..."
            value={usuarioFiltro}
            onChange={(e) => setUsuarioFiltro(e.target.value)}
          />
        </div>
      </div>

      {erro       ? <div className="feedback-banner feedback-banner--error">{erro}</div> : null}
      {erroConfig ? <div className="feedback-banner feedback-banner--error">{erroConfig}</div> : null}

      <section className="content-card">
        {loading && sessoes.length === 0 ? (
          <div className="content-card--centered">
            <div className="screen-loader__spinner" />
            <p>Carregando sessoes...</p>
          </div>
        ) : sessoesVisiveis.length === 0 ? (
          <div className="content-card--centered">
            <i className="bi bi-people" style={{ fontSize: 32, opacity: 0.4 }} />
            <p>Ninguem ativo com esses filtros agora.</p>
          </div>
        ) : (
          <div className="table-shell">
            <table className="portal-table">
              <thead>
                <tr>
                  <th style={{ width: 24 }} />
                  <th>Usuario</th>
                  <th>Sistema</th>
                  <th>Filial</th>
                  <th>Perfil</th>
                  <th>Ultimo endpoint</th>
                  <th>Sessao</th>
                  <th>Ultimo ping</th>
                </tr>
              </thead>
              <tbody>
                {sessoesVisiveis.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <span className={`monitor-uso__bolha monitor-uso__bolha--${s._cor}`} title={`Status: ${s._cor}`} />
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{s.usuario_nome || `Usuario #${s.usuario_id}`}</div>
                      <div style={{ fontSize: '0.78em', opacity: 0.65 }}>id {s.usuario_id}</div>
                    </td>
                    <td>{SISTEMA_LABEL[s.sistema] || s.sistema}</td>
                    <td>{s.filial || '—'}</td>
                    <td>{s.perfil || '—'}</td>
                    <td title={s.pagina_atual || ''}>
                      <code style={{ fontSize: '0.85em' }}>{s.pagina_atual || '—'}</code>
                    </td>
                    <td>{duracaoSessao(s.primeiro_heartbeat, s.ultimo_heartbeat)}</td>
                    <td>{tempoRelativo(s._seg)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <ConfigMonitorUsoModal
        aberto={configAberta}
        onClose={() => setConfigAberta(false)}
        config={config}
        atualizar={atualizar}
        resetar={resetar}
        PRESET={PRESET}
      />
    </div>
  );
}
